import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { downloadProficiencyTemplate, importProficiencyRecords } from "../../lib/api";

function currentSemester() {
  const d = new Date();
  const m = d.getMonth();
  const y = d.getFullYear();
  if (m >= 7) return { semester: `Fall-${y}`, school_year: `${y}-${y + 1}` };
  return { semester: `Spring-${y}`, school_year: `${y - 1}-${y}` };
}

export default function ProficiencyImportCard() {
  const [{ semester, school_year }, setPeriod] = useState(currentSemester());
  const [indicatorType, setIndicatorType] = useState("all");
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownload = () => {
    const url = downloadProficiencyTemplate(semester, school_year, indicatorType);
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `Proficiency_Import_${semester}_${school_year}.xlsx`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Template downloaded");
      })
      .catch(() => toast.error("Download failed"));
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Choose a file first");
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const res = await importProficiencyRecords(file, semester, school_year);
      setResult(res.data);
      toast.success(`Imported ${res.data.saved} record${res.data.saved === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card className="mt-4" data-testid="proficiency-import-card">
      <CardHeader>
        <CardTitle>Bulk Import / Historical Excel</CardTitle>
        <CardDescription>
          Download a pre-filled Excel template with student numbers and indicator numbers, fill in scores in Excel, then upload it back. Useful for historical data or end-of-semester batch entry.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Semester</Label>
            <Input value={semester} onChange={(e) => setPeriod((p) => ({ ...p, semester: e.target.value }))} data-testid="import-semester-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">School Year</Label>
            <Input value={school_year} onChange={(e) => setPeriod((p) => ({ ...p, school_year: e.target.value }))} data-testid="import-year-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Indicator Type</Label>
            <Select value={indicatorType} onValueChange={setIndicatorType}>
              <SelectTrigger data-testid="import-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All indicators</SelectItem>
                <SelectItem value="academic">Academic only</SelectItem>
                <SelectItem value="transferable">Transferable only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleDownload} variant="outline" data-testid="import-download-btn">📥 Download Template</Button>
        </div>
        <div className="border-t pt-3 space-y-2">
          <Label>Upload filled file</Label>
          <Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
          <Button onClick={handleUpload} disabled={importing || !file} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="import-upload-btn">
            {importing ? "Importing…" : "Upload & Import"}
          </Button>
        </div>
        {result && (
          <div className="border rounded-md p-3 text-sm bg-muted/30" data-testid="import-result">
            <div><strong>{result.saved}</strong> records imported · <strong>{result.skipped}</strong> skipped</div>
            {result.error_count > 0 && (
              <details className="text-xs text-red-700 mt-1">
                <summary>{result.error_count} errors (click to expand)</summary>
                <ul className="list-disc pl-5">
                  {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
